import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * GoogleButton — "Continue with Google" button.
 *
 * Uses Supabase's built-in OAuth flow. The browser redirects to
 * accounts.google.com, then back to / with a session in the URL hash.
 *
 * To enable this you must:
 *   1. Enable the Google provider in your Supabase dashboard
 *      (Authentication → Providers → Google).
 *   2. Create a Google OAuth Client at console.cloud.google.com
 *      (APIs & Services → Credentials → Create OAuth Client).
 *   3. Paste the Client ID + Secret into the Supabase provider config.
 *   4. Add https://<your-domain>/ to "Authorized redirect URIs" on
 *      the Google side (the Supabase callback URL is shown there).
 *
 * See: https://supabase.com/docs/guides/auth/social-login/auth-google
 */
export function GoogleButton({ mode = 'signin', onAuthed, redirectTo }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      // Lazy-load supabase to keep this component independent
      const { supabase } = await import('../supabaseClient');
      const callback = redirectTo || (window.location.origin + '/');
      const { data, error: e } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: callback },
      });
      if (e) throw e;
      // Browser will redirect to Google — no further code runs here
      // unless the OAuth flow is configured for popup mode.
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setError(e.message || 'Google sign-in failed');
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2.5 h-10 px-4 rounded-md bg-surface border border-border text-sm font-medium text-fg hover:bg-surface-2 hover:border-fg-subtle transition-colors disabled:opacity-50"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        {mode === 'signup' ? 'Sign up with Google' : 'Continue with Google'}
      </button>
      {error && (
        <div className="mt-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}

function GoogleIcon() {
  // Google's official "G" mark in currentColor-friendly form.
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.28 1.4-1.07 2.59-2.27 3.4v2.81h3.66c2.16-1.99 3.43-4.91 3.43-8.45z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.15 0 5.78-1.05 7.71-2.83l-3.66-2.81c-1.02.68-2.32 1.09-4.05 1.09-3.11 0-5.75-2.1-6.69-4.93H1.49v3.04A11.99 11.99 0 0 0 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.31 14.43A7.18 7.18 0 0 1 4.92 12c0-.85.14-1.67.39-2.43V6.53H1.49A11.99 11.99 0 0 0 0 12c0 1.94.47 3.77 1.49 5.47l3.82-3.04z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.78 1.19 15.13 0 12 0A11.99 11.99 0 0 0 1.49 6.53l3.82 3.04C6.25 6.85 8.89 4.75 12 4.75z"
      />
    </svg>
  );
}